import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import UserManagement from '../features/user/screens/UserManagement.jsx';
import TaskManagement from '../features/tasks/screens/TaskManagement.jsx';
import FormManagement from '../features/forms/screens/FormManagement.jsx';
import CancelledTasksManagement from '../features/tasks/screens/CancelledTasksManagement.jsx';
import FormResponseManagement from '../features/forms/screens/FormResponseManagement.jsx';
import TeamManagement from '../features/teams/screens/TeamManagement.jsx';
import HistoricsTeams from '../features/teams/screens/HistoricsTeams.jsx';
import EquiposPage from '../features/teams/screens/EquiposPage.jsx';
import AdminHome from '../features/admin-home/screens/AdminHome.jsx';
import TaskTypeManagement from '../features/tasks/screens/TaskTypeManagement.jsx';
import CategoryManagement from '../features/tasks/screens/CategoryManagement.jsx';
import TaskDistribution from '../features/tasks/screens/TaskDistribution.jsx';
import ActivityLogs from '../features/audit/screens/ActivityLogs.jsx';
import ActiveUsers from '../features/audit/components/ActiveUsersPanel.jsx';
import TeamDisableManagement from '../features/teams/screens/TeamDisableManagement.jsx';
import GuardiasPage from '../features/teams/screens/GuardTeam';
import ViewTask from '../features/tasks/screens/ViewTask.jsx';
import FileExplorerScreen from '../features/file-explorer/screens/FileExplorerScreen.jsx';
import MyPlanScreen from '../features/my-plan/screens/MyPlanScreen.jsx';
import CompanySettings from '../features/companies/screens/CompanySettings.jsx';
import { myCompanyService } from '../services/companies/myCompanyService.js';
import { jwtDecode } from "jwt-decode";
import { useResponsive } from '../hooks/useResponsive';
import { useGuidedTour, useGuidedTourStep } from '../shared/hooks/useGuidedTour.js';
import TourButton from '../shared/components/ui/TourButton.jsx';
import { homeTour } from '../features/admin-home/homeTour.js';
import { teamsTour } from '../features/teams/teamsTour.js';
import { calendarTour } from '../features/tasks/calendarTour.js';
import { formsTour } from '../features/forms/formsTour.js';
import { taskListTour } from '../features/tasks/taskListTour.js';
import { taskDetailTour } from '../features/tasks/taskDetailTour.js';
import { taskTypesTour, categoriesTour } from '../features/tasks/taskTypesTour.js';

// Recorridos guiados por vista (activeTab). Para sumar uno: pasos + data-tour + una entrada aquí.
// Gestión de Tareas tiene uno por modo (Lista / Calendario), elegido con el modo guardado en el
// store, y otro para el detalle de una tarea.
const TOURS = {
  inicio: homeTour,
  'gestion-equipos': teamsTour,
  'gestion-tareas': { list: taskListTour, calendar: calendarTour, detail: taskDetailTour },
  formularios: formsTour,
  tipo_tareas: taskTypesTour,
  categorias: categoriesTour,
};
import { AuthContext } from '../context/authContext.jsx';
import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { useTaskManagementFiltersStore } from '../stores/taskManagementFiltersStore.js';


import { getAuthToken, getUserFromToken, clearAuth } from '../context/authContext';
import {
  Users,
  Car,
  ClipboardText,
  XCircle,
  FileText,
  Clipboard,
  ChartBar,
  CaretLeft,
  CaretRight,
  SignOut,
  UsersThree,
  UsersFour,
  ClockCounterClockwise,
  Lightning,
  MagnifyingGlass,
  ListBullets,
  User,
  Eye,
  List,
  CalendarX,
  Shield,
  House,
  GearSix,
  StarIcon,
  Buildings,
  Package,
  FolderOpen,
  Receipt,
  MapPin,
  Tag

} from '@phosphor-icons/react';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('inicio');
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState('');
  const [userCompany, setUserCompany] = useState('');
  const [companyIcon, setCompanyIcon] = useState(null);
  const [companyIconUrl, setCompanyIconUrl] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveUsers, setShowActiveUsers] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [equiposSubmenuOpen, setEquiposSubmenuOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  // Ubicación del explorador desde la que se abrió el detalle de tarea (null si
  // se llegó por otro lado): el "volver" del detalle regresa ahí.
  const [fileExplorerReturn, setFileExplorerReturn] = useState(null);
  const [tasksSubmenuOpen, setTasksSubmenuOpen] = useState(false);
  const [miEmpresaSubmenuOpen, setMiEmpresaSubmenuOpen] = useState(false);
  const { isAuthenticated, user } = useContext(AuthContext);
  const resetTaskManagementFilters = useTaskManagementFiltersStore((state) => state.resetFilters);
  const setStoredViewMode = useTaskManagementFiltersStore((state) => state.setViewMode);
  const taskViewMode = useTaskManagementFiltersStore((state) => state.viewMode);
  const navigate = useNavigate();

  // Hook responsive mejorado según Tekla.io
  const { isMobile, isTablet, isDesktop, getResponsiveClass } = useResponsive();

  // El recorrido abre el submenú del paso y muestra el sidebar en los pasos de secciones.
  // Solo los pasos del sidebar tocan los submenús: el resto deja el menú como está.
  const handleTourStep = useCallback((step) => {
    if (step?.sidebar) {
      setSidebarOpen(true);
      setEquiposSubmenuOpen(step.submenu === 'equipos');
      setTasksSubmenuOpen(step.submenu === 'tareas');
      setMiEmpresaSubmenuOpen(step.submenu === 'mi-empresa');
    }
    if (isMobile) setMobileSidebarOpen(Boolean(step?.sidebar));
  }, [isMobile]);
  useGuidedTourStep(handleTourStep);

  // La validación de primer login (isFirstLogin) aplica solo a Inicio; el resto se abre con el botón "?".
  const tour = useGuidedTour({
    steps: activeTab === 'gestion-tareas'
      ? TOURS[activeTab][selectedTaskId ? 'detail' : taskViewMode]
      : TOURS[activeTab],
    autoStart: activeTab === 'inicio' && Boolean(user?.isFirstLogin),
  });

  // Detectar rol y nombre del usuario una sola vez al montar el componente
  useEffect(() => {
    const detectUserInfo = () => {
      try {
        // Primero intentar obtener desde las cookies usando la utilidad
        const userFromToken = getUserFromToken();
        if (userFromToken) {
          const role = userFromToken.role;
          const name = userFromToken.name || 'Usuario';
          setUserCompany(userFromToken.companyName);
          setUserRole(role);
          setUserName(name);
          return;
        }

        // Fallback al método anterior para compatibilidad
        const token = getAuthToken();
        if (token) {
          const decoded = jwtDecode(token);
          const role = decoded.role;
          // Priorizar el nombre real del usuario, no el email
          const name = decoded.name || decoded.firstName || decoded.fullName || 'Usuario';
          
          setUserRole(role);
          setUserName(name);
        }
      } catch (error) {
        console.error('Error detecting user info:', error);
        // En caso de error, asumir admin
        setUserRole('admin');
        setUserName('Usuario');
      }
    };

    detectUserInfo();
  }, []);

  // El nombre del token puede quedar desactualizado luego de un cambio de empresa.
  // La empresa actual y su icono se resuelven desde la API para el encabezado.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let cancelled = false;

    const loadCompanyBrand = async () => {
      try {
        const { company } = await myCompanyService.getMyCompany();
        if (cancelled) return;

        setUserCompany(company.name || '');

        const icon = await myCompanyService.getMyCompanyIcon();
        if (cancelled) return;
        setCompanyIcon(icon);
      } catch (error) {
        // Conserva el nombre disponible en el token y el fallback visual si
        // la empresa no puede consultarse al montar el dashboard.
        console.error('Error loading company brand:', error);
      }
    };

    loadCompanyBrand();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!companyIcon) {
      setCompanyIconUrl('');
      return undefined;
    }

    const iconUrl = URL.createObjectURL(companyIcon);
    setCompanyIconUrl(iconUrl);
    return () => URL.revokeObjectURL(iconUrl);
  }, [companyIcon]);

  const handleLogout = async () => {
    try {
      // Registrar logout en el backend
      const token = getAuthToken();
      await fetch('/api/audit/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      clearAuth();
      //navigate('/admin/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Funciones para el sidebar móvil
  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setMobileSidebarOpen(false);
  };

  const isTaskDetailView = activeTab === 'gestion-tareas' && Boolean(selectedTaskId);

  // Cerrar sidebar móvil al cambiar de tab
  const handleTabChange = (tabId) => {
    if (activeTab === 'gestion-tareas' && tabId !== 'gestion-tareas') {
      resetTaskManagementFilters();
      setStoredViewMode('list');
    }

    setActiveTab(tabId);
    // Siempre se sale del detalle de tarea: así, "Gestión de Tareas" desde el detalle vuelve al listado.
    setSelectedTaskId(null);
    setFileExplorerReturn(null);
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  // Evitar scroll del body cuando el menú móvil está abierto
  useEffect(() => {
    if (mobileSidebarOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [mobileSidebarOpen]);

  useEffect(() => {
    return () => {
      resetTaskManagementFilters();
    };
  }, [resetTaskManagementFilters]);

  // Definición única de tabs: cada tab (y cada item de submenu) declara qué
  // roles pueden verlo. Para sumar un rol nuevo (ej: "supervisor") alcanza con
  // agregarlo al array `roles` correspondiente, sin duplicar estructura.
  const ROLE_ADMIN = ['admin', 'superadmin'];
  const ROLE_ALL = ['admin', 'superadmin', 'operator'];

  const ALL_TABS = [
    { id: 'inicio', name: 'Inicio', icon: <ChartBar size={20} />, roles: ROLE_ALL },
    { id: 'usuarios', name: 'Usuarios', icon: <Users size={20} />, roles: ROLE_ADMIN },
    {
      id: 'equipos',
      name: 'Equipos',
      icon: <UsersFour size={20} />,
      hasSubmenuTeam: true,
      roles: ROLE_ALL,
      submenu: [
        { id: 'flota-vehiculos', name: 'Flota Vehículos', icon: <Car size={20} />, roles: ROLE_ALL },
        { id: 'gestion-equipos', name: 'Gestión Equipos', icon: <UsersThree size={20} />, roles: ROLE_ADMIN },
        { id: 'guardias-equipos', name: 'Guardias', icon: <Shield size={20} />, roles: ROLE_ALL },
        { id: 'historial-equipos', name: 'Historial Equipos', icon: <ClockCounterClockwise size={20} />, roles: ROLE_ADMIN },
        { id: 'inhabilitaciones-equipos', name: 'Inhabilitaciones', icon: <CalendarX size={20} />, roles: ROLE_ADMIN },
      ]
    },
    {
      id: 'tareas',
      name: 'Tareas',
      icon: <ClipboardText size={20} />,
      hasSubmenuTasks: true,
      roles: ROLE_ALL,
      submenu: [
        { id: 'gestion-tareas', name: 'Gestión de Tareas', icon: <ClipboardText size={20} />, roles: ROLE_ALL },
        { id: 'distribuir-tareas', name: 'Distribuir Tareas', icon: <MapPin size={20} />, roles: ROLE_ADMIN },
        { id: 'tipo_tareas', name: 'Tipo de Tareas', icon: <GearSix size={20} />, roles: ROLE_ADMIN },
        { id: 'categorias', name: 'Categorías', icon: <Tag size={20} />, roles: ROLE_ADMIN },
      ]
    },
    { id: 'respuesta-formularios', name: 'Respuesta Formularios', icon: <FileText size={20} />, roles: ROLE_ALL },
    { id: 'formularios', name: 'Formularios', icon: <Clipboard size={20} />, roles: ROLE_ADMIN },
    // { id: 'métricas', name: 'Métricas', icon: <ChartBar size={20} /> },
    { id: 'logs-actividad', name: 'Logs de Actividad', icon: <ListBullets size={20} />, roles: ROLE_ADMIN },
    {
      id: 'mi-empresa',
      name: 'Mi empresa',
      icon: <Buildings size={20} />,
      hasSubmenuEmpresa: true,
      roles: ROLE_ADMIN,
      submenu: [
        { id: 'plan-empresa', name: 'Plan', icon: <Package size={20} />, roles: ROLE_ADMIN },
        { id: 'configuracion-empresa', name: 'Configuración', icon: <GearSix size={20} />, roles: ROLE_ADMIN },
        { id: 'explorador-archivos', name: 'Explorador de archivos', icon: <FolderOpen size={20} />, roles: ROLE_ADMIN },
        { id: 'facturacion-empresa', name: 'Facturación', icon: <Receipt size={20} />, disabled: true, roles: ROLE_ADMIN },
      ]
    },
  ];

  const visibleTabs = ALL_TABS
    .filter((tab) => tab.roles.includes(userRole))
    .map((tab) => tab.submenu
      ? { ...tab, submenu: tab.submenu.filter((subItem) => subItem.roles.includes(userRole)) }
      : tab);

  if (!isAuthenticated) {
    return (
      <div className="container">
        <Navigate to="/admin/login" replace />
      </div>
    )
  }
  return (
    <div className="h-screen bg-bg dashboard-layout flex overflow-hidden">
      {/* Botón hamburguesa para móviles */}
      {isMobile && !mobileSidebarOpen && !isTaskDetailView && (
        <button
          onClick={toggleMobileSidebar}
          className="mobile-menu-toggle"
          title="Abrir menú"
        >
          <List size={20} />
        </button>
      )}

      {/* Overlay para móviles */}
      {isMobile && (
        <div
          className={`sidebar-overlay ${mobileSidebarOpen ? 'active' : ''}`}
          onClick={closeMobileSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 left-0 h-screen bg-ink-2 text-content transition-all duration-300 ease-in-out dashboard-sidebar ${sidebarOpen ? 'w-72' : 'w-20'} ${isMobile ? (mobileSidebarOpen ? 'translate-x-0 z-50 mobile-open' : '-translate-x-full z-50') : 'translate-x-0 z-30'} flex flex-col`}>
        {/* Header del Sidebar */}
        <div className="p-4 border-b border-ink-3 flex items-center justify-between">
          {sidebarOpen ? (
            <>
              <div className="flex min-w-0 items-center space-x-2">
                <img
                  src={companyIconUrl || '/generic-logo-white.png'}
                  alt={`Icono de ${userCompany || 'la empresa'}`}
                  className="h-8 w-8 shrink-0 object-contain"
                />
                <span className="truncate text-lg font-semibold">{userCompany || 'Mi empresa'}</span>
              </div>
              <div className="flex items-center space-x-2">
                {isMobile && (
                  <button
                    onClick={closeMobileSidebar}
                    className="text-content-muted hover:text-content transition-colors lg:hidden"
                    title="Cerrar menú"
                  >
                    <XCircle size={20} />
                  </button>
                )}
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="text-content-muted hover:text-content transition-colors hidden lg:block"
                >
                  <CaretLeft size={20} />
                </button>
              </div>
            </>
          ) : (
            <div className="flex w-full items-center justify-between gap-2">
              <img
                src={companyIconUrl || '/generic-logo-white.png'}
                alt={`Icono de ${userCompany || 'la empresa'}`}
                className="h-8 w-8 object-contain"
              />
              <button
                onClick={() => setSidebarOpen(true)}
                className="text-content-muted hover:text-content transition-colors"
                aria-label="Expandir menú"
              >
                <CaretRight size={20} />
              </button>
            </div>
          )}

        </div>

        {userRole === 'superadmin' && (
          <div className="pt-2 px-2">
            <button
              onClick={() => navigate('/superadmin/dashboard')}
              className={`w-full flex items-center ${sidebarOpen ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-lg transition-colors text-accent-text hover:bg-[--app-sidebar-active] hover:text-accent-text`}
              title={!sidebarOpen ? 'Panel Superadmin' : ''}
            >
              <StarIcon size={16} />
              {sidebarOpen && <span className="text-sm font-medium">Panel Superadmin{userCompany ? ` (${userCompany})` : ''}</span>}
            </button>
          </div>
        )}

        {/* Menú de navegación */}
        <div data-tour-scroll className="flex-1 overflow-y-auto p-4 space-y-4">
          <nav className="">
            <ul className="space-y-2">
              {visibleTabs.map((tab) => (
                <li key={tab.id} data-tour={`nav-${tab.id}`}>
                  {tab.hasSubmenuTeam || tab.hasSubmenuTasks || tab.hasSubmenuEmpresa ? (
                    <>
                      <button
                        onClick={() => {
                          const isTeamMenu = tab.hasSubmenuTeam;
                          const isTasksMenu = tab.hasSubmenuTasks;
                          const isEmpresaMenu = tab.hasSubmenuEmpresa;

                          if (!sidebarOpen) {
                            setSidebarOpen(true);
                            if (isTeamMenu) setEquiposSubmenuOpen(true);
                            if (isTasksMenu) setTasksSubmenuOpen(true);
                            if (isEmpresaMenu) setMiEmpresaSubmenuOpen(true);
                            return;
                          }

                          if (isTeamMenu) {
                            setEquiposSubmenuOpen(!equiposSubmenuOpen);
                          }

                          if (isTasksMenu) {
                            setTasksSubmenuOpen(!tasksSubmenuOpen);
                          }

                          if (isEmpresaMenu) {
                            setMiEmpresaSubmenuOpen(!miEmpresaSubmenuOpen);
                          }
                        }}
                        className={`w-full flex items-center ${sidebarOpen ? 'space-x-3 px-3' : 'justify-center px-2'
                          } py-2 rounded-lg transition-colors relative ${tab.submenu?.some(sub => sub.id === activeTab)
                            ? sidebarOpen
                              ? 'bg-[--app-sidebar-active] text-accent-text'
                              : 'text-accent-text'
                            : 'text-content-muted hover:bg-ink-3 hover:text-content'
                          }`}
                        title={!sidebarOpen ? tab.name : ''}
                      >
                        {!sidebarOpen && tab.submenu?.some(sub => sub.id === activeTab) && (
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber rounded-r-full"></div>
                        )}
                        <span className="flex-shrink-0">{tab.icon}</span>
                        {sidebarOpen && (
                          <>
                            <span className="text-sm font-medium flex-1 text-left">{tab.name}</span>
                            <CaretRight
                              size={16}
                              className={`transition-transform ${(tab.hasSubmenuTeam && equiposSubmenuOpen) ||
                                (tab.hasSubmenuTasks && tasksSubmenuOpen) ||
                                (tab.hasSubmenuEmpresa && miEmpresaSubmenuOpen)
                                ? 'rotate-90'
                                : ''
                                }`}
                            />
                          </>
                        )}
                      </button>
                      {sidebarOpen &&
                        ((tab.hasSubmenuTeam && equiposSubmenuOpen) ||
                          (tab.hasSubmenuTasks && tasksSubmenuOpen) ||
                          (tab.hasSubmenuEmpresa && miEmpresaSubmenuOpen)) &&
                        tab.submenu && (
                          <ul className="ml-4 mt-1 space-y-1">
                            {tab.submenu.map((subItem) => (
                              <li key={subItem.id} data-tour={`nav-${subItem.id}`}>
                                <button
                                  onClick={subItem.disabled ? undefined : () => handleTabChange(subItem.id)}
                                  disabled={subItem.disabled}
                                  title={subItem.disabled ? 'Próximamente' : ''}
                                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors text-sm ${subItem.disabled
                                    ? 'text-content-muted opacity-60 cursor-not-allowed'
                                    : activeTab === subItem.id
                                      ? 'bg-[--app-sidebar-active] text-accent-text'
                                      : 'text-content-muted hover:bg-ink-3 hover:text-content'
                                    }`}
                                >
                                  <span className="flex-shrink-0">{subItem.icon}</span>
                                  <span>{subItem.name}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                    </>
                  ) : (
                    <button
                      onClick={() => handleTabChange(tab.id)}
                      className={`w-full flex items-center ${sidebarOpen ? 'space-x-3 px-3' : 'justify-center px-2'
                        } py-2 rounded-lg transition-colors relative ${activeTab === tab.id
                          ? sidebarOpen
                            ? 'bg-[--app-sidebar-active] text-accent-text'
                            : 'text-accent-text'
                          : 'text-content-muted hover:bg-ink-3 hover:text-content'
                        }`}
                      title={!sidebarOpen ? tab.name : ''}
                    >
                      {!sidebarOpen && activeTab === tab.id && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber rounded-r-full"></div>
                      )}
                      <span className="flex-shrink-0">{tab.icon}</span>
                      {sidebarOpen && <span className="text-sm font-medium">{tab.name}</span>}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Usuarios en Línea - debajo del último ítem del menú */}
          {(userRole === 'admin' || userRole === 'superadmin' || userRole === 'operator') && showActiveUsers && (
            <div className="border-t border-ink-3 pt-4">
              <ActiveUsers showToggle={false} sidebarOpen={sidebarOpen} />
            </div>
          )}
        </div>

        {/* Botón Usuarios en Línea - PARTE INFERIOR para admin y operador */}
        {(userRole === 'admin' || userRole === 'superadmin' || userRole === 'operator') && (
          <div className="pt-2">
            <button
              onClick={() => setShowActiveUsers(!showActiveUsers)}
              className={`w-full flex items-center ${sidebarOpen ? 'space-x-3 px-3' : 'justify-center px-2'
                } py-2 rounded-lg transition-colors relative ${showActiveUsers
                  ? sidebarOpen
                    ? 'bg-[--app-sidebar-active] text-accent-text'
                    : 'text-accent-text'
                  : 'text-content-muted hover:bg-ink-3 hover:text-content'
                }`}
              title={!sidebarOpen ? 'Usuarios en Línea' : ''}
            >
              {/* Línea indicadora cuando sidebar está colapsado */}
              {!sidebarOpen && showActiveUsers && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber rounded-r-full"></div>
              )}
              <span className="flex-shrink-0"><Eye size={20} /></span>
              {sidebarOpen && <span className="text-sm font-medium">Usuarios en Línea</span>}
            </button>
          </div>
        )}

        {/* Perfil de usuario - FIXED POSITION */}
        <div className="p-4 border-t border-ink-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-surface-2 rounded-full flex items-center justify-center">
              <User size={16} />
            </div>
            {sidebarOpen && (
              <div className="flex-1">
                <p className="text-sm font-medium text-content">
                  {userName}
                </p>
                <p className="text-xs text-content-muted">
                  {userRole === 'operator' ? 'Operador' : userRole === 'superadmin' ? 'Superadmin' : 'Administrador'}
                </p>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="text-content-muted hover:text-content transition-colors"
              title={!sidebarOpen ? 'Cerrar Sesión' : ''}
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="flex-1 bg-bg dashboard-main-content">
        {activeTab === 'inicio' && <AdminHome />}
        {activeTab !== 'inicio' && (
          <div
            className="rounded-lg text-content table-responsive page-content page-content-no-shadow"
          >
            {activeTab === 'usuarios' && userRole !== 'operator' && <UserManagement />}
            {activeTab === 'equipos' && <EquiposPage />}
            {activeTab === 'flota-vehiculos' && <EquiposPage />}
            {activeTab === 'gestion-equipos' && userRole !== 'operator' && <TeamManagement />}
            {activeTab === 'guardias-equipos' && <GuardiasPage />}
            {activeTab === 'historial-equipos' && userRole !== 'operator' && <HistoricsTeams />}
            {activeTab === 'inhabilitaciones-equipos' && userRole !== 'operator' && <TeamDisableManagement />}
              {activeTab === 'gestion-tareas' && (
                selectedTaskId ? (
                  <ViewTask
                    taskId={selectedTaskId}
                    onBack={() => {
                      setSelectedTaskId(null);
                      if (fileExplorerReturn) {
                        setActiveTab('explorador-archivos');
                      }
                    }}
                    onViewTask={setSelectedTaskId}
                  />
                ) : (
                  <TaskManagement onViewTask={setSelectedTaskId} />
                )
              )}
            {activeTab === 'distribuir-tareas' && <TaskDistribution />}
            {activeTab === 'tipo_tareas' && <TaskTypeManagement />}
            {activeTab === 'categorias' && userRole !== 'operator' && <CategoryManagement userRole={userRole} />}
            {activeTab === 'respuesta-formularios' && <FormResponseManagement />}
            {activeTab === 'formularios' && userRole !== 'operator' && <FormManagement />}
            {activeTab === 'logs-actividad' && userRole !== 'operator' && <ActivityLogs />}
            {activeTab === 'plan-empresa' && userRole !== 'operator' && <MyPlanScreen/>}
            {activeTab === 'configuracion-empresa' && userRole !== 'operator' && (
              <CompanySettings
                onIconChanged={setCompanyIcon}
                onNameChanged={setUserCompany}
              />
            )}
            {activeTab === 'explorador-archivos' && userRole !== 'operator' && (
              <FileExplorerScreen
                userRole={userRole}
                initialLocation={fileExplorerReturn}
                onViewTask={(taskId, location) => {
                  // Directo y no vía handleTabChange: ese limpia selectedTaskId
                  // y la ubicación de retorno.
                  setFileExplorerReturn(location);
                  setActiveTab('gestion-tareas');
                  setSelectedTaskId(taskId);
                }}
              />
            )}
          </div>
        )}
      </div>

      {tour.hasTour && <TourButton onClick={tour.start} />}
    </div>
  );
};

export default AdminDashboard;
